import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Terminal,
  Package,
  Sparkles,
  Cloud,
  Layers,
  Code,
  CheckCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { features } from "../mockData";

const iconMap = {
  Package,
  Terminal,
  Sparkles,
  Cloud,
  Layers,
  Code,
};

const Home = () => {
  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 md:py-32">
        {/* Background */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="text-center space-y-8">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium">
            <Sparkles className="h-4 w-4" />
            C# Framework & CLI for Clean Architecture
          </div>

          {/* Main Heading */}
          <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight">
            Build Clean .NET Apps
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">
              Faster
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-slate-400 max-w-3xl mx-auto">
            AxisNet — Enterprise-grade C# library and a powerful CLI tool
            designed to generate your clean architecture features instantly.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <Link to="/docs/getting-started">
              <Button
                size="lg"
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-lg shadow-emerald-500/50 transition-all hover:scale-105"
              >
                Get Started
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/docs/cli">
              <Button
                size="lg"
                variant="outline"
                className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
              >
                <Terminal className="mr-2 h-5 w-5" />
                Install CLI
              </Button>
            </Link>
          </div>

          {/* Quick Install */}
          <div className="pt-8">
            <div className="inline-block mr-2">
              <div className="px-6 py-3 rounded-lg bg-slate-900/80 border border-slate-800 backdrop-blur-sm">
                <code className="text-emerald-400 font-mono text-sm">
                  dotnet add package AxisNet.Framework
                </code>
              </div>
            </div>

            <div className="inline-block ml-2">
              <div className="px-6 py-3 rounded-lg bg-slate-900/80 border border-slate-800 backdrop-blur-sm">
                <code className="text-emerald-400 font-mono text-sm">
                  dotnet tool install --global AxisNet.Cli
                </code>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Everything You Need
          </h2>
          <p className="text-lg text-slate-400">
            Two powerful tools working together seamlessly
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
          {features.map((feature, idx) => {
            const Icon = iconMap[feature.icon];
            return (
              <Card
                key={idx}
                className="p-6 bg-slate-900/50 border-slate-800 hover:border-emerald-500/50 transition-all hover:shadow-lg hover:shadow-emerald-500/10 group"
              >
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-400 group-hover:bg-emerald-500/20 transition-colors">
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-slate-400">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Two Pillars Section */}
      <section className="py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            The Complete Ecosystem
          </h2>
          <p className="text-lg text-slate-400">
            AxisNet Library + AxisNet CLI
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* C# Library */}
          <Card className="p-8 bg-gradient-to-br from-slate-900/80 to-slate-900/50 border-slate-800 hover:border-emerald-500/50 transition-all">
            <div className="flex items-center justify-center h-16 w-16 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white mb-6 mx-auto shadow-lg shadow-emerald-500/50">
              <Package className="h-8 w-8" />
            </div>
            <h3 className="text-xl font-bold text-white text-center mb-4">
              C# Library
            </h3>
            <p className="text-slate-400 text-center mb-6">
              Enterprise-grade foundation with base classes, interfaces, helpers
              and DDD-ready structures.
            </p>
            <ul className="space-y-2 text-sm text-slate-400">
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-400" />
                Base entities & aggregates
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-400" />
                Value objects
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-400" />
                Repository patterns
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-400" />
                Domain events
              </li>
            </ul>
            <Link to="/docs/library">
              <Button
                variant="link"
                className="mt-6 text-emerald-400 hover:text-emerald-300 p-0"
              >
                Learn more <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </Card>

          {/* CLI Tool */}
          <Card className="p-8 bg-gradient-to-br from-slate-900/80 to-slate-900/50 border-emerald-500/30 hover:border-emerald-500/50 transition-all shadow-lg shadow-emerald-500/10">
            <div className="flex items-center justify-center h-16 w-16 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white mb-6 mx-auto shadow-lg shadow-emerald-500/50">
              <Terminal className="h-8 w-8" />
            </div>
            <h3 className="text-xl font-bold text-white text-center mb-4">
              AxisNet CLI
            </h3>
            <p className="text-slate-400 text-center mb-6">
              Scaffold features, entities and handlers instantly with a single
              command.
            </p>
            <ul className="space-y-2 text-sm text-slate-400">
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-400" />
                Instant scaffolding
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-400" />
                Template system
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-400" />
                Code generation
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-400" />
                Project automation
              </li>
            </ul>
            <Link to="/docs/cli">
              <Button
                variant="link"
                className="mt-6 text-emerald-400 hover:text-emerald-300 p-0"
              >
                Learn more <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <Card className="p-12 bg-gradient-to-br from-emerald-900/20 to-teal-900/20 border-emerald-500/30 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Build Something Amazing?
          </h2>
          <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
            Developers use AxisNet to build enterprise-grade .NET applications
            faster with clean architecture.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/docs/getting-started">
              <Button
                size="lg"
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-lg shadow-emerald-500/50 transition-all hover:scale-105"
              >
                Get Started Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </Card>
      </section>
    </div>
  );
};

export default Home;
